// src/components/game/MemoryGame.jsx
// The interactive memory-grid puzzle.
//
// Puzzle ownership rule:
//   LevelRunner generates ONE puzzle (target + playerGrid) and passes them
//   as props. This component NEVER calls generateGrid() itself.
//   On Reset, it calls onReset() so LevelRunner regenerates and re-passes.

import React, { useState, useCallback } from 'react';
import { getColorStyle } from '../../utils/randomGrid';
import { useSound } from '../../hooks/useSound';

const MEMORY_MESSAGES = [
  'Tap a prism to start!',
  'Good job! Keep going! 🎉',
  'Nice move!',
  'Almost there, keep it up!',
  "You're on fire! 🔥",
  'Great placement!',
  'Getting closer!',
  'Looking good!',
  'One step closer!',
  "Don't stop now!",
  'So close!',
  'Amazing! Nearly there!',
];

/**
 * @param {object}   props
 * @param {Array}    props.targetGrid   — canonical target (from LevelRunner)
 * @param {Array}    props.initialPlayer — starting player arrangement (from LevelRunner)
 * @param {object}   props.config       — full level config
 * @param {Function} props.onWin        — (stars: number) => void
 * @param {Function} props.onLose       — () => void
 * @param {Function} props.onReset      — () => void  (tells parent to regenerate puzzle)
 * @param {boolean}  props.gameOver     — true once win/lose has fired
 */
const MemoryGame = ({ targetGrid, initialPlayer, config, onWin, onLose, onReset, gameOver }) => {
  const { playHover, playClick, playDrop } = useSound();
  const { moves: moveLimit } = config.difficulty;

  const [playerGrid, setPlayerGrid] = useState(() => [...initialPlayer]);
  const [selected,   setSelected]   = useState(null);
  const [moves,      setMoves]      = useState(0);
  const [msgIndex,   setMsgIndex]   = useState(0);
  const [msgPop,     setMsgPop]     = useState(false);
  const [moveFlash,  setMoveFlash]  = useState(false);
  const [flashMatch, setFlashMatch] = useState(false);
  const [flashDef,   setFlashDef]   = useState(false);

  // When LevelRunner sends a fresh puzzle (after reset), sync local player state
  // We use a key on LevelRunner's side instead of syncing here — see LevelRunner.

  const bumpMessage = useCallback(() => {
    setMsgIndex(i => {
      const next = (i + 1) % MEMORY_MESSAGES.length;
      return next === 0 ? 1 : next;
    });
    setMsgPop(true);
    setTimeout(() => setMsgPop(false), 400);
  }, []);

  const handleCellClick = (idx) => {
    if (gameOver) return;
    playClick();

    if (selected === null) {
      // Only select cells that have a prism
      if (playerGrid[idx] !== null) setSelected(idx);
      return;
    }

    // Deselect if tapping same cell
    if (selected === idx) {
      setSelected(null);
      return;
    }

    // Swap selected ↔ idx
    const newGrid = [...playerGrid];
    [newGrid[selected], newGrid[idx]] = [newGrid[idx], newGrid[selected]];
    const newMoves = moves + 1;

    setPlayerGrid(newGrid);
    setSelected(null);
    setMoves(newMoves);
    playDrop();
    bumpMessage();

    setMoveFlash(true);
    setTimeout(() => setMoveFlash(false), 300);

    // Check win
    const isWin = targetGrid.every((t, i) => newGrid[i] === t);
    if (isWin) {
      setFlashMatch(true);
      const earnedStars =
        newMoves <= Math.ceil(moveLimit * 0.5) ? 3
        : newMoves <= moveLimit               ? 2
        : 1;
      onWin(earnedStars);
      return;
    }

    // Check lose
    if (newMoves >= moveLimit) {
      setFlashDef(true);
      onLose();
    }
  };

  const handleReset = () => {
    if (gameOver) return;
    playClick();
    // Delegate to parent — parent regenerates puzzle and re-mounts this component via key
    onReset();
  };

  const threeStarThreshold = Math.ceil(moveLimit * 0.5);

  return (
    <>
      {flashMatch && <div className="lvl1-match-flash" />}
      {flashDef   && <div className="lvl1-defeat-flash" />}

      {/* Reset button */}
      <div style={{ position: 'absolute', top: 80, right: 24, zIndex: 10 }}>
        <button className="lvl1-reset-btn" onClick={handleReset} onMouseEnter={playHover}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
               strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
            <path d="M3 3v5h5"/>
          </svg>
          RESET
        </button>
      </div>

      {/* Character sidebar */}
      <div className="lvl1-left">
        <div className="lvl1-character-wrap">
          <div className="missionchar1" style={{ width: 120, height: 120 }} />
          <div className="lvl1-char-bubble">
            <div className="lvl1-char-name">PRISMA</div>
            <div className={`lvl1-char-msg ${msgPop ? 'msg-pop' : ''}`}>
              {MEMORY_MESSAGES[msgIndex]}
            </div>
          </div>
        </div>
      </div>

      {/* Player grid */}
      <div className="lvl1-center">
        <div className={`lvl1-moves ${moveFlash ? 'flash' : ''}`} style={{ marginBottom: 8 }}>
          Moves: <span>{moves}</span>
          <span className="lvl1-moves-limit"> / {moveLimit}</span>
        </div>
        <div className="lvl1-grid-label">YOUR GRID</div>
        <div className="lvl1-grid-container">
          <div className="lvl1-grid">
            {playerGrid.map((colorId, i) => {
              const c = colorId ? getColorStyle(colorId) : null;
              return (
                <div
                  key={i}
                  className={[
                    'lvl1-player-cell',
                    colorId ? 'has-prism' : '',
                    selected === i ? 'selected' : '',
                  ].join(' ')}
                  onClick={() => handleCellClick(i)}
                  onMouseEnter={playHover}
                >
                  {c && (
                    <div
                      className="lvl1-prism"
                      style={{
                        background: c.bg,
                        boxShadow: `0 0 18px ${c.glow}55, inset 0 1px 0 rgba(255,255,255,0.2)`,
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Info panels */}
      <div className="lvl1-right">
        <div className="lvl1-panel">
          <div className="lvl1-panel-title">HOW TO PLAY</div>
          <ul className="lvl1-howto-list">
            <li>Tap any colored prism to select it</li>
            <li>Tap another cell to swap it</li>
            <li>Recreate the pattern you saw at the start</li>
            <li>Complete in {moveLimit} moves or fewer!</li>
          </ul>
        </div>
        <div className="lvl1-panel">
          <div className="lvl1-panel-title">STARS</div>
          <div className="lvl1-stars-list">
            <div className="lvl1-star-row">
              <span>Under {threeStarThreshold} moves</span>
              <span className="lvl1-star-icon" style={{ color: '#FFD930', fontFamily: "'Segoe UI Symbol','Apple Color Emoji','Noto Sans Symbols',Arial,sans-serif" }}>★★★</span>
            </div>
            <div className="lvl1-star-row">
              <span>Under {moveLimit} moves</span>
              <span className="lvl1-star-icon" style={{ color: '#18B8E8', fontFamily: "'Segoe UI Symbol','Apple Color Emoji','Noto Sans Symbols',Arial,sans-serif" }}>★★</span>
            </div>
            <div className="lvl1-star-row">
              <span>Complete level</span>
              <span className="lvl1-star-icon" style={{ color: '#FF2D9A', fontFamily: "'Segoe UI Symbol','Apple Color Emoji','Noto Sans Symbols',Arial,sans-serif" }}>★</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MemoryGame;